/* eslint react/jsx-pascal-case: 0 */
import { Box } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  MRT_GlobalFilterTextField,
  MRT_LinearProgressBar,
  MRT_TablePagination,
  MRT_ToolbarAlertBanner,
  MRT_ToolbarDropZone,
  MRT_ToolbarInternalButtons,
  MRT_TopToolbarProps,
} from 'material-react-table';
import type { MRT_RowData } from 'material-react-table/src/types';
import { getCommonToolbarStyles } from 'material-react-table/src/utils/style.utils';
import { parseFromValuesOrFunc } from 'material-react-table/src/utils/utils';

// it is a customized copy of original component src/components/toolbar/MRT_TopToolbar.tsx
// Changes:
// - global filter positioned to the left. `positionGlobalFilter` option is ignored.
export const MrtTopToolbar = <TData extends MRT_RowData>({
  table,
}: MRT_TopToolbarProps<TData>) => {
  const {
    getState,
    options: {
      enableGlobalFilter,
      enablePagination,
      enableToolbarInternalActions,
      muiTopToolbarProps,
      positionPagination,
      positionToolbarAlertBanner,
      positionToolbarDropZone,
      renderTopToolbarCustomActions,
    },
    refs: { topToolbarRef },
  } = table;

  const { isFullScreen, showGlobalFilter } = getState();

  const isMobile = useMediaQuery('(max-width:720px)');
  const isTablet = useMediaQuery('(max-width:1024px)');

  const toolbarProps = parseFromValuesOrFunc(muiTopToolbarProps, { table });

  const stackAlertBanner =
    isMobile || !!renderTopToolbarCustomActions || (showGlobalFilter && isTablet);

  const globalFilterProps = {
    sx: !isTablet
      ? {
          zIndex: 2,
        }
      : undefined,
    table,
  };

  return (
    <Box
      {...toolbarProps}
      ref={(ref: HTMLDivElement) => {
        topToolbarRef.current = ref;
        if (toolbarProps?.ref) {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          toolbarProps.ref.current = ref;
        }
      }}
      sx={(theme) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return {
          ...getCommonToolbarStyles({ table, theme }),
          position: isFullScreen ? 'sticky' : 'relative',
          top: isFullScreen ? '0' : undefined,
          // eslint-disable-next-line  @typescript-eslint/no-explicit-any
          ...(parseFromValuesOrFunc(toolbarProps?.sx, theme) as any),
        };
      }}
    >
      {positionToolbarAlertBanner === 'top' && (
        <MRT_ToolbarAlertBanner stackAlertBanner={stackAlertBanner} table={table} />
      )}
      {['both', 'top'].includes(positionToolbarDropZone ?? '') && (
        <MRT_ToolbarDropZone table={table} />
      )}
      <Box
        sx={{
          alignItems: 'flex-start',
          boxSizing: 'border-box',
          display: 'flex',
          gap: '0.5rem',
          justifyContent: 'right',
          p: '0.5rem',
          position: stackAlertBanner ? 'relative' : 'absolute',
          right: 0,
          top: 0,
          width: '100%',
        }}
      >
        {enableToolbarInternalActions ? (
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              flexWrap: 'wrap-reverse',
              gap: '0.5rem',
              justifyContent: 'flex-end',
            }}
          >
            {enableGlobalFilter ? <MRT_GlobalFilterTextField {...globalFilterProps} /> : null}
            <MRT_ToolbarInternalButtons table={table} />
          </Box>
        ) : (
          enableGlobalFilter && <MRT_GlobalFilterTextField {...globalFilterProps} />
        )}
        {renderTopToolbarCustomActions?.({ table }) ?? <span />}
      </Box>
      {enablePagination && ['both', 'top'].includes(positionPagination ?? '') ? (
        <MRT_TablePagination position="top" table={table} />
      ) : null}
      <MRT_LinearProgressBar isTopToolbar table={table} />
    </Box>
  );
};
